import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@theraply/ui';
import { ChevronLeftIcon, CheckIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import {
  useRegistrationWizard,
  useStepForm,
  basicInfoSchema,
  professionalInfoSchema,
  documentUploadSchema,
  practiceDetailsSchema,
  reviewSchema,
} from '../../hooks/useRegistrationWizard';
import { BasicInfoStep } from './steps/BasicInfoStep';
import { ProfessionalInfoStep } from './steps/ProfessionalInfoStep';
import { DocumentUploadStep } from './steps/DocumentUploadStep';
import { PracticeDetailsStep } from './steps/PracticeDetailsStep';
import { ReviewStep } from './steps/ReviewStep';

const STEPS = [
  { id: 0, name: 'Basic Info', description: 'Contact information' },
  { id: 1, name: 'Professional Info', description: 'License & credentials' },
  { id: 2, name: 'Documents', description: 'Upload verification' },
  { id: 3, name: 'Practice Details', description: 'Specialties & rates' },
  { id: 4, name: 'Review', description: 'Review & submit' },
];

export function RegistrationWizard() {
  const navigate = useNavigate();
  const {
    currentStep,
    totalSteps,
    goToStep,
    nextStep,
    previousStep,
    formData,
    updateFormData,
    submitRegistration,
    isSubmitting,
  } = useRegistrationWizard();

  // Individual step forms
  const basicInfoForm = useStepForm(basicInfoSchema, formData.basicInfo);
  const professionalInfoForm = useStepForm(professionalInfoSchema, formData.professionalInfo);
  const documentUploadForm = useStepForm(documentUploadSchema, formData.documentUpload);
  const practiceDetailsForm = useStepForm(practiceDetailsSchema, formData.practiceDetails);
  const reviewForm = useStepForm(reviewSchema, formData.review);

  // Sync form values with wizard state
  useEffect(() => {
    basicInfoForm.reset(formData.basicInfo);
  }, [formData.basicInfo]);

  useEffect(() => {
    professionalInfoForm.reset(formData.professionalInfo);
  }, [formData.professionalInfo]);

  useEffect(() => {
    documentUploadForm.reset(formData.documentUpload);
  }, [formData.documentUpload]);

  useEffect(() => {
    practiceDetailsForm.reset(formData.practiceDetails);
  }, [formData.practiceDetails]);

  useEffect(() => {
    reviewForm.reset(formData.review);
  }, [formData.review]);

  const handleNext = async () => {
    let isValid = false;

    switch (currentStep) {
      case 0:
        isValid = await basicInfoForm.trigger();
        if (isValid) {
          updateFormData('basicInfo', basicInfoForm.getValues());
        }
        break;
      case 1:
        isValid = await professionalInfoForm.trigger();
        if (isValid) {
          updateFormData('professionalInfo', professionalInfoForm.getValues());
        }
        break;
      case 2:
        isValid = await documentUploadForm.trigger();
        if (isValid) {
          updateFormData('documentUpload', documentUploadForm.getValues());
        }
        break;
      case 3:
        isValid = await practiceDetailsForm.trigger();
        if (isValid) {
          updateFormData('practiceDetails', practiceDetailsForm.getValues());
        }
        break;
      default:
        isValid = true;
    }

    if (isValid) {
      nextStep();
    }
  };

  const handlePrevious = () => {
    // Save current step data before going back
    switch (currentStep) {
      case 0:
        updateFormData('basicInfo', basicInfoForm.getValues());
        break;
      case 1:
        updateFormData('professionalInfo', professionalInfoForm.getValues());
        break;
      case 2:
        updateFormData('documentUpload', documentUploadForm.getValues());
        break;
      case 3:
        updateFormData('practiceDetails', practiceDetailsForm.getValues());
        break;
      case 4:
        updateFormData('review', reviewForm.getValues());
        break;
    }
    previousStep();
  };

  const handleEditStep = (step: number) => {
    // Save review data before navigating
    updateFormData('review', reviewForm.getValues());
    goToStep(step);
  };

  const handleSubmit = async () => {
    try {
      await submitRegistration();
      // Navigate to success page or dashboard
      navigate('/onboarding/success');
    } catch (error) {
      console.error('Submission error:', error);
      alert('Failed to submit application. Please try again.');
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <BasicInfoStep form={basicInfoForm} />;
      case 1:
        return <ProfessionalInfoStep form={professionalInfoForm} />;
      case 2:
        return <DocumentUploadStep form={documentUploadForm} />;
      case 3:
        return <PracticeDetailsStep form={practiceDetailsForm} />;
      case 4:
        return (
          <ReviewStep
            form={reviewForm}
            formData={formData}
            onEditStep={handleEditStep}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Therapist Registration</h1>
              <p className="mt-1 text-sm text-gray-500">Complete all steps to join our platform</p>
            </div>
            <div className="text-sm text-gray-500">
              Step {currentStep + 1} of {totalSteps}
            </div>
          </div>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Progress">
            <ol className="flex items-center justify-between py-6">
              {STEPS.map((step, index) => {
                const isCompleted = index < currentStep;
                const isCurrent = index === currentStep;

                return (
                  <li key={step.id} className="relative flex-1">
                    <div className="flex flex-col items-center">
                      <div
                        className={clsx(
                          'flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors',
                          isCompleted
                            ? 'border-primary-600 bg-primary-600'
                            : isCurrent
                            ? 'border-primary-600 bg-white'
                            : 'border-gray-300 bg-white'
                        )}
                      >
                        {isCompleted ? (
                          <CheckIcon className="h-6 w-6 text-white" />
                        ) : (
                          <span
                            className={clsx(
                              'text-sm font-semibold',
                              isCurrent ? 'text-primary-600' : 'text-gray-500'
                            )}
                          >
                            {index + 1}
                          </span>
                        )}
                      </div>
                      <div className="mt-2 text-center hidden sm:block">
                        <p
                          className={clsx(
                            'text-sm font-medium',
                            isCurrent ? 'text-primary-600' : 'text-gray-500'
                          )}
                        >
                          {step.name}
                        </p>
                        <p className="text-xs text-gray-400">{step.description}</p>
                      </div>
                    </div>
                    {index < STEPS.length - 1 && (
                      <div
                        className={clsx(
                          'absolute top-5 left-1/2 hidden h-0.5 w-full sm:block',
                          isCompleted ? 'bg-primary-600' : 'bg-gray-300'
                        )}
                        style={{ transform: 'translateY(-50%)' }}
                      />
                    )}
                  </li>
                );
              })}
            </ol>
          </nav>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8">
          {renderStep()}
        </div>

        {/* Navigation Buttons */}
        {currentStep < 4 && (
          <div className="mt-8 flex items-center justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={handlePrevious}
              disabled={currentStep === 0}
            >
              <ChevronLeftIcon className="h-5 w-5 mr-1" />
              Previous
            </Button>

            <Button type="button" onClick={handleNext}>
              {currentStep === totalSteps - 1 ? 'Review Application' : 'Continue'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
